import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Context/AuthContext';
import { PerformanceService } from '../services/performanceService';

export const usePerformance = () => {
  const { user } = useAuth();
  const [performanceService, setPerformanceService] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('Overall');
  const [performanceData, setPerformanceData] = useState(null);
  const [videoLinks, setVideoLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [campaignGoals, setCampaignGoals] = useState(null);
  const [selectedCampaignData, setSelectedCampaignData] = useState(null);

  // Initialize performance service when user changes
  useEffect(() => {
    if (user?.uid) {
      const service = new PerformanceService(user.uid);
      setPerformanceService(service);
    }
  }, [user]);

  // Load campaigns
  const loadCampaigns = useCallback(async () => {
    if (!performanceService) return;
    
    try {
      setLoading(true);
      const campaignsData = await performanceService.getCampaigns();
      setCampaigns(campaignsData);
      
      // Set first campaign as selected if none selected
      if (campaignsData.length > 0 && selectedCampaign === 'Overall') {
        setSelectedCampaign(campaignsData[0].id);
      }
    } catch (err) {
      setError('Failed to load campaigns');
      console.error('Error loading campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, [performanceService, selectedCampaign]);

  // Load performance data for selected campaign
  const loadPerformanceData = useCallback(async (campaignId, filter = 'All') => {
    if (!performanceService || !campaignId) return;
    
    try {
      setLoading(true);
      const data = await performanceService.getPerformanceData(campaignId, filter);
      const metrics = performanceService.calculateMetrics(data);
      
      setPerformanceData({
        raw: data,
        metrics,
        filter
      });
    } catch (err) {
      setError('Failed to load performance data');
      console.error('Error loading performance data:', err);
    } finally {
      setLoading(false);
    }
  }, [performanceService]);

  // Load video links
  const loadVideoLinks = useCallback(async (campaignId) => {
    if (!performanceService || !campaignId) return;
    
    try {
      const links = await performanceService.getVideoLinks(campaignId);
      setVideoLinks(links);
    } catch (err) {
      console.error('Error loading video links:', err);
    }
  }, [performanceService]);

  // Load campaign goals
  const loadCampaignGoals = useCallback(async (campaignId) => {
    if (!performanceService || !campaignId) return;
    
    try {
      const goalsData = await performanceService.getCampaignGoals(campaignId);
      setCampaignGoals(goalsData);
    } catch (err) {
      console.error('Error loading campaign goals:', err);
    }
  }, [performanceService]);

  // Load campaign data
  const loadCampaignData = useCallback(async (campaignId) => {
    if (!performanceService || !campaignId) return;
    
    try {
      const campaignData = await performanceService.getCampaignWithGoals(campaignId);
      setSelectedCampaignData(campaignData);
    } catch (err) {
      console.error('Error loading campaign data:', err);
    }
  }, [performanceService]);

  // Generate AI insights
  const generateInsights = useCallback(async (campaignId) => {
    if (!performanceService || !campaignId || !performanceData) return;
    
    try {
      setLoading(true);
      const insights = await performanceService.generateAIInsights(
        campaignId, 
        performanceData.raw
      );
      setAiInsights(insights);
    } catch (err) {
      setError('Failed to generate AI insights');
      console.error('Error generating insights:', err);
    } finally {
      setLoading(false);
    }
  }, [performanceService, performanceData]);

  // Add video link
  const addVideoLink = useCallback(async (url, campaignId) => {
    if (!performanceService || !url || !campaignId) return;
    
    try {
      setLoading(true);
      const newLink = await performanceService.addVideoLink({
        url,
        campaignId,
        platform: detectPlatform(url)
      });
      
      setVideoLinks(prev => [newLink, ...prev]);
      return newLink;
    } catch (err) {
      setError('Failed to add video link');
      console.error('Error adding video link:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [performanceService]);

  // Update campaign goals
  const updateCampaignGoals = useCallback(async (campaignId, goalData) => {
    if (!performanceService || !campaignId) return;
    
    try {
      await performanceService.updateCampaignGoals(campaignId, goalData);
      setCampaignGoals(goalData);
      // Refresh campaign data to get updated information
      await loadCampaignData(campaignId);
    } catch (err) {
      setError('Failed to update campaign goals');
      console.error('Error updating campaign goals:', err);
      throw err;
    }
  }, [performanceService, loadCampaignData]);

  // Connect social platform
  const connectPlatform = useCallback(async (platform, credentials) => {
    if (!performanceService) return;
    
    try {
      setLoading(true);
      const result = await performanceService.connectSocialPlatform(platform, credentials);
      return result;
    } catch (err) {
      setError(`Failed to connect ${platform}`);
      console.error('Error connecting platform:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [performanceService]);

  // Fetch analytics for a video
  const fetchVideoAnalytics = useCallback(async (platform, videoUrl) => {
    if (!performanceService) return;
    
    try {
      setLoading(true);
      const analytics = await performanceService.fetchPlatformAnalytics(platform, videoUrl);
      return analytics;
    } catch (err) {
      setError('Failed to fetch video analytics');
      console.error('Error fetching analytics:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [performanceService]);

  // Effect to load data when campaign changes
  useEffect(() => {
    if (selectedCampaign && selectedCampaign !== 'Overall') {
      loadPerformanceData(selectedCampaign);
      loadVideoLinks(selectedCampaign);
      loadCampaignGoals(selectedCampaign);
      loadCampaignData(selectedCampaign);
    } else {
      // Clear campaign-specific data when "Overall" is selected
      setCampaignGoals(null);
      setSelectedCampaignData(null);
    }
  }, [selectedCampaign, loadPerformanceData, loadVideoLinks, loadCampaignGoals, loadCampaignData]);

  // Effect to load campaigns on mount
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Helper function to detect platform from URL
  const detectPlatform = (url) => {
    if (url.includes('tiktok.com')) return 'TikTok';
    if (url.includes('instagram.com')) return 'Instagram';
    if (url.includes('facebook.com')) return 'Facebook';
    if (url.includes('youtube.com')) return 'YouTube';
    return 'Unknown';
  };

  return {
    // State
    campaigns,
    selectedCampaign,
    performanceData,
    videoLinks,
    loading,
    error,
    aiInsights,
    campaignGoals,
    selectedCampaignData,
    
    // Actions
    setSelectedCampaign,
    loadCampaigns,
    loadPerformanceData,
    loadVideoLinks,
    loadCampaignGoals,
    loadCampaignData,
    generateInsights,
    addVideoLink,
    updateCampaignGoals,
    connectPlatform,
    fetchVideoAnalytics,
    
    // Utilities
    detectPlatform,
    clearError: () => setError(null)
  };
};
